import { useEffect, useState } from "react";

export type HostSurface = "web" | "desktop";

type DesktopWindow = Window & {
  excellentDesktop?: { isDesktop?: boolean };
};

function isDesktopChromePreview(): boolean {
  const env = (import.meta as ImportMeta & {
    env?: { DEV?: boolean; MODE?: string };
  }).env;
  const qaBuild = env?.DEV === true || env?.MODE === "test";
  return qaBuild && new URLSearchParams(window.location.search).get("desktopchrome") === "1";
}

/**
 * Host identity comes from the read-only Electron preload capability. The
 * query parameter is deliberately limited to development/test browser QA; the
 * `data-desktop-chrome` HTML attribute is presentation state and must never be
 * allowed to promote a web page into the desktop host.
 */
export function readHostSurface(): HostSurface {
  if (typeof window === "undefined") return "web";
  const desktopWindow = window as DesktopWindow;
  return desktopWindow.excellentDesktop?.isDesktop || isDesktopChromePreview() ? "desktop" : "web";
}

export function useHostSurface(): HostSurface {
  const [surface, setSurface] = useState<HostSurface>(readHostSurface);

  useEffect(() => {
    const sync = () => setSurface(readHostSurface());
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  return surface;
}
